import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Building2, Download, Plus } from 'lucide-react';
import CreateTenantModal from '../components/CreateTenantModal';
import { useAccess } from '../context/AuthContext';
import { useApi } from '../lib/useApi';
import { ago, bytes, date, limit, num } from '../lib/format';
import { Bar, Button, Card, Chip, Empty, ErrorBanner, PageHeader, PageSkeleton, PLAN_TONE, SearchBox, Segmented, Select, TenantLogo, Tone } from '../components/ui';

export interface PlanInfo {
  plan: string; label: string; plan_status: string; max_vendors: number; max_users: number; max_indicators: number;
  indicators_included: number; indicators_total: number; all_indicators: boolean; trial_end_date: string | null;
  // Override: the preset the tenant is on and extra vendors / users granted on top of it (limits above already include them).
  tier?: string | null; extra_vendors?: number; extra_users?: number;
}

/** "+2 vendors, +1 user" for a plan with an override, else ''. */
export function overrideText(p?: Pick<PlanInfo, 'extra_vendors' | 'extra_users'> | null): string {
  const part = (n: number | undefined, word: string) => (n ? `+${n} ${word}${n === 1 ? '' : 's'}` : '');
  return [part(p?.extra_vendors, 'vendor'), part(p?.extra_users, 'user')].filter(Boolean).join(', ');
}
interface TenantRow {
  id: string; company_name: string; subdomain: string; is_active: boolean; created_at: string; domain: string | null; logo_url: string | null;
  owner: { name: string; email: string } | null; plan: PlanInfo;
  usage: { users: number; active_users: number; vendors: number; scans_30d: number; last_scan_at: string | null;
           last_login_at: string | null; vendors_pct: number | null; users_pct: number | null };
  flags: string[];
  storage?: { total_bytes: number; database_bytes: number; file_bytes: number; files: number };
  source: { kind: SourceKind; created_by: string | null };
  sizing?: { company_size: string | null; vendors_managed: string | null };
}

// Same ranges FluxWatch's signup form offers (backend/services/signup_profile.py there).
const COMPANY_SIZES = ['1-50', '51-200', '201-1000', '1001-5000', '5000+'];
const VENDORS_MANAGED = ['1-10', '11-50', '51-200', '201-500', '500+'];

// Where a workspace came from: signed up on the website, claimed from the vendor portal rating, or created by the team.
export type SourceKind = 'self' | 'vendor_portal' | 'admin';
export const SOURCE_INFO: Record<SourceKind, { label: string; tone: Tone }> = {
  self: { label: 'Self sign-up', tone: 'slate' },
  vendor_portal: { label: 'Vendor portal', tone: 'purple' },
  admin: { label: 'Created by team', tone: 'blue' },
};

export const FLAG_INFO: Record<string, { label: string; tone: Tone }> = {
  inactive: { label: 'Deactivated', tone: 'slate' },
  trial_expired: { label: 'Trial expired', tone: 'red' },
  trial_ending: { label: 'Trial ending', tone: 'amber' },
  vendors_at_limit: { label: 'Vendor limit reached', tone: 'red' },
  vendors_near_limit: { label: 'Near vendor limit', tone: 'amber' },
  users_at_limit: { label: 'User limit reached', tone: 'red' },
  users_near_limit: { label: 'Near user limit', tone: 'amber' },
  dormant: { label: 'Dormant', tone: 'slate' },
};

export function statusChip(isActive: boolean, plan: PlanInfo) {
  if (!isActive) return <Chip tone="slate" dot>Deactivated</Chip>;
  if (plan.plan_status === 'trial') {
    const end = plan.trial_end_date ? new Date(plan.trial_end_date).getTime() : null;
    if (end && end < Date.now()) return <Chip tone="red" dot>Trial expired</Chip>;
    return <Chip tone="amber" dot>Trial</Chip>;
  }
  return <Chip tone="green" dot>Active</Chip>;
}

type SortKey = 'name' | 'created' | 'vendors' | 'users' | 'scans' | 'activity' | 'storage';
const csvCell = (v: unknown) => { const s = String(v ?? ''); const safe = /^[=+\-@]/.test(s) ? `'${s}` : s; return `"${safe.replace(/"/g, '""')}"`; };

export default function Tenants() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') || 'all') as 'all' | 'active' | 'trial' | 'inactive';
  const [plan, setPlan] = useState('all');
  const [flag, setFlag] = useState('');
  const [source, setSource] = useState<'' | SourceKind>('');
  const [size, setSize] = useState('');
  const [vendorsManaged, setVendorsManaged] = useState('');
  const [creating, setCreating] = useState(false);
  const { canWrite } = useAccess();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'created', dir: -1 });
  const { data, error, loading, reload } = useApi<{ tenants: TenantRow[]; indicator_total: number }>('/tenants');

  const all = data?.tenants || [];
  const counts = useMemo(() => ({
    all: all.length,
    active: all.filter(t => t.is_active).length,
    trial: all.filter(t => t.plan.plan_status === 'trial').length,
    inactive: all.filter(t => !t.is_active).length,
  }), [all]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = all.filter(t => {
      if (status === 'active' && !t.is_active) return false;
      if (status === 'inactive' && t.is_active) return false;
      if (status === 'trial' && t.plan.plan_status !== 'trial') return false;
      if (plan !== 'all' && t.plan.label !== plan) return false;
      if (flag && !t.flags.some(f => f.startsWith(flag))) return false;
      if (source && t.source?.kind !== source) return false;
      if (size && (t.sizing?.company_size || 'unknown') !== size) return false;
      if (vendorsManaged && (t.sizing?.vendors_managed || 'unknown') !== vendorsManaged) return false;
      if (q && ![t.company_name, t.subdomain, t.owner?.email, t.owner?.name].join(' ').toLowerCase().includes(q)) return false;
      return true;
    });
    const val = (t: TenantRow): number | string => ({
      name: t.company_name.toLowerCase(), created: t.created_at || '', vendors: t.usage.vendors, users: t.usage.users,
      scans: t.usage.scans_30d, activity: t.usage.last_login_at || '', storage: t.storage?.total_bytes || 0,
    }[sort.key]);
    return [...list].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * sort.dir);
  }, [all, status, plan, flag, source, size, vendorsManaged, search, sort]);

  const exportCsv = () => {
    const head = ['Tenant', 'Subdomain', 'Owner', 'Owner email', 'Plan', 'Status', 'Company size', 'Vendors they manage', 'Vendors', 'Vendor limit', 'Users', 'User limit', 'Scans (30d)', 'Storage (bytes)', 'Last sign-in', 'Created'];
    const lines = rows.map(t => [t.company_name, t.subdomain, t.owner?.name, t.owner?.email, t.plan.label, t.is_active ? t.plan.plan_status : 'deactivated',
      t.sizing?.company_size || '', t.sizing?.vendors_managed || '',
      t.usage.vendors, limit(t.plan.max_vendors), t.usage.users, limit(t.plan.max_users), t.usage.scans_30d, t.storage?.total_bytes || 0, t.usage.last_login_at || '', t.created_at].map(csvCell).join(','));
    const blob = new Blob(['﻿' + [head.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `fluxwatch-tenants-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorBanner text={error} onRetry={reload} />;

  const Th = ({ k, children, className = '' }: { k?: SortKey; children: React.ReactNode; className?: string }) => (
    <th className={`whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium text-slate-500 ${className}`}>
      {k ? (
        <button onClick={() => setSort(s => ({ key: k, dir: s.key === k ? (s.dir === 1 ? -1 : 1) : -1 }))} className="inline-flex items-center gap-1 hover:text-slate-900">
          {children}{sort.key === k && (sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
        </button>
      ) : children}
    </th>
  );

  const usageCell = (used: number, max: number, pct: number | null) => (
    <div className="w-28">
      <div className="mb-1 text-xs text-slate-600"><b className="font-semibold text-slate-900">{num(used)}</b> / {limit(max)}</div>
      {max === -1 ? <div className="text-[11px] text-slate-400">No limit</div> : <Bar pct={pct} />}
    </div>
  );

  return (
    <div>
      <PageHeader title="Tenants" subtitle={`${num(counts.all)} workspaces · every scan runs ${num(data?.indicator_total)} checks on paid plans`}
        actions={<>
          <Button icon={Download} onClick={exportCsv} disabled={!rows.length}>Export CSV</Button>
          {canWrite && <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>New tenant</Button>}
        </>} />
      {creating && <CreateTenantModal onClose={() => { setCreating(false); reload(); }} onCreated={() => reload()} />}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented value={status} onChange={v => setParams(v === 'all' ? {} : { status: v })}
          options={[{ key: 'all', label: 'All', count: counts.all }, { key: 'active', label: 'Active', count: counts.active },
                    { key: 'trial', label: 'Trial', count: counts.trial }, { key: 'inactive', label: 'Deactivated', count: counts.inactive }]} />
        <Select value={plan} onChange={e => setPlan(e.target.value)} className="!w-40">
          <option value="all">All plans</option>
          {['Tropos', 'Strato', 'Meso', 'Thermo', 'Exo', 'Custom'].map(p => <option key={p}>{p}</option>)}
        </Select>
        <Select value={flag} onChange={e => setFlag(e.target.value)} className="!w-48">
          <option value="">Any condition</option>
          <option value="trial_">Trial ending or expired</option>
          <option value="vendors_">Near vendor limit</option>
          <option value="users_">Near user limit</option>
          <option value="dormant">Dormant (30+ days)</option>
        </Select>
        <Select value={source} onChange={e => setSource(e.target.value as '' | SourceKind)} className="!w-44">
          <option value="">Any source</option>
          <option value="self">Self sign-up</option>
          <option value="vendor_portal">Vendor portal</option>
          <option value="admin">Created by team</option>
        </Select>
        <Select value={size} onChange={e => setSize(e.target.value)} className="!w-44">
          <option value="">Any company size</option>
          {COMPANY_SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
          <option value="unknown">Not asked</option>
        </Select>
        <Select value={vendorsManaged} onChange={e => setVendorsManaged(e.target.value)} className="!w-48">
          <option value="">Any vendor count</option>
          {VENDORS_MANAGED.map(s => <option key={s} value={s}>{s} vendors managed</option>)}
          <option value="unknown">Not asked</option>
        </Select>
        <div className="ml-auto"><SearchBox value={search} onChange={setSearch} placeholder="Search tenant, owner or email" /></div>
      </div>

      <Card bodyClass="p-0">
        {rows.length === 0 ? (
          <Empty icon={Building2} title="No tenants match" text="Try a different filter or search." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="border-b border-slate-100 bg-slate-50/60">
                <tr>
                  <Th k="name">Tenant</Th><Th>Plan</Th><Th>Size</Th><Th k="vendors">Vendors</Th><Th k="users">Users</Th>
                  <Th k="scans" className="text-right">Scans · 30d</Th><Th k="storage" className="text-right">Storage</Th><Th k="activity">Last sign-in</Th><Th>Attention</Th><Th k="created">Created</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(t => (
                  <tr key={t.id} onClick={() => navigate(`/tenants/${t.id}`)} className="cursor-pointer hover:bg-slate-50">
                    <td className="max-w-[300px] px-4 py-3">
                      <div className="flex items-center gap-3">
                        <TenantLogo name={t.company_name} domain={t.domain} logoUrl={t.logo_url} size={34} />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">{t.company_name}</div>
                          <div className="truncate text-xs text-slate-500">{t.owner ? `${t.owner.name || ''} · ${t.owner.email}` : t.subdomain}</div>
                          {t.source && t.source.kind !== 'self' && (
                            <div className="mt-1"><Chip tone={SOURCE_INFO[t.source.kind]?.tone || 'slate'}>{SOURCE_INFO[t.source.kind]?.label || t.source.kind}</Chip></div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><div className="flex flex-col items-start gap-1"><Chip tone={PLAN_TONE[t.plan.label] || 'slate'}>{t.plan.label}</Chip>{overrideText(t.plan) && <span className="text-[11px] font-medium text-violet-700">{overrideText(t.plan)}</span>}{statusChip(t.is_active, t.plan)}</div></td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600">
                      {t.sizing?.company_size || t.sizing?.vendors_managed ? (
                        <>
                          <div><b className="font-medium text-slate-900">{t.sizing?.company_size || '—'}</b> employees</div>
                          <div className="mt-0.5"><b className="font-medium text-slate-900">{t.sizing?.vendors_managed || '—'}</b> vendors</div>
                        </>
                      ) : <span className="text-slate-400">Not asked</span>}
                    </td>
                    <td className="px-4 py-3">{usageCell(t.usage.vendors, t.plan.max_vendors, t.usage.vendors_pct)}</td>
                    <td className="px-4 py-3">{usageCell(t.usage.users, t.plan.max_users, t.usage.users_pct)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">{num(t.usage.scans_30d)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-slate-700" title={t.storage ? `${bytes(t.storage.database_bytes)} data · ${bytes(t.storage.file_bytes)} in ${t.storage.files} files` : ''}>{bytes(t.storage?.total_bytes || 0)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{ago(t.usage.last_login_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex max-w-[220px] flex-wrap gap-1">
                        {t.flags.filter(f => f !== 'inactive').map(f => <Chip key={f} tone={FLAG_INFO[f]?.tone || 'slate'}>{FLAG_INFO[f]?.label || f}</Chip>)}
                        {t.flags.filter(f => f !== 'inactive').length === 0 && <span className="text-xs text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{date(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
